import {errorToString} from "@cdx/common";
import {refusalReason} from "../../lib/mutation-refusal";

// `PublicKeyCredential.parseCreationOptionsFromJSON` and `toJSON()` are missing before Safari 18.4
// and Chrome 129, so each has a manual base64url fallback.

const toBase64url = (buffer: ArrayBuffer) => {
  let binary = "";
  for (const byte of new Uint8Array(buffer)) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

const fromBase64url = (value: string) => {
  const base64 = value.replace(/-/g, "+").replace(/_/g, "/");
  const binary = atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, "="));
  return Uint8Array.from(binary, (char) => char.charCodeAt(0));
};

const toDescriptor = (descriptor: PublicKeyCredentialDescriptorJSON) =>
  ({
    ...descriptor,
    id: fromBase64url(descriptor.id),
  }) as PublicKeyCredentialDescriptor;

const hasJSONHelpers = () => typeof PublicKeyCredential.parseCreationOptionsFromJSON === "function";

const parseCreationOptions = (
  options: PublicKeyCredentialCreationOptionsJSON
): PublicKeyCredentialCreationOptions => {
  if (hasJSONHelpers()) return PublicKeyCredential.parseCreationOptionsFromJSON(options);
  return {
    ...options,
    challenge: fromBase64url(options.challenge),
    user: {...options.user, id: fromBase64url(options.user.id)},
    excludeCredentials: options.excludeCredentials?.map(toDescriptor),
  } as PublicKeyCredentialCreationOptions;
};

const parseRequestOptions = (
  options: PublicKeyCredentialRequestOptionsJSON
): PublicKeyCredentialRequestOptions => {
  if (hasJSONHelpers()) return PublicKeyCredential.parseRequestOptionsFromJSON(options);
  return {
    ...options,
    challenge: fromBase64url(options.challenge),
    allowCredentials: options.allowCredentials?.map(toDescriptor),
  } as PublicKeyCredentialRequestOptions;
};

/** The shape `node-api/src/context/auth/webauthn.ts` parses. */
export const credentialToJSON = (credential: PublicKeyCredential) => {
  if (hasJSONHelpers()) return credential.toJSON();
  const {response} = credential;
  const envelope = {
    id: credential.id,
    rawId: toBase64url(credential.rawId),
    type: credential.type,
    clientExtensionResults: credential.getClientExtensionResults(),
  };
  if ("attestationObject" in response) {
    const attestation = response as AuthenticatorAttestationResponse;
    return {
      ...envelope,
      response: {
        clientDataJSON: toBase64url(attestation.clientDataJSON),
        attestationObject: toBase64url(attestation.attestationObject),
        transports: attestation.getTransports?.() ?? [],
      },
    };
  }
  const assertion = response as AuthenticatorAssertionResponse;
  return {
    ...envelope,
    response: {
      clientDataJSON: toBase64url(assertion.clientDataJSON),
      authenticatorData: toBase64url(assertion.authenticatorData),
      signature: toBase64url(assertion.signature),
      userHandle: assertion.userHandle ? toBase64url(assertion.userHandle) : null,
    },
  };
};

const ensureCredential = (credential: Credential | null) =>
  credential ? (credential as PublicKeyCredential) : Promise.reject("No credential was returned.");

export const createPasskey = async (options: PublicKeyCredentialCreationOptionsJSON) =>
  ensureCredential(await navigator.credentials.create({publicKey: parseCreationOptions(options)}));

export const getPasskeyAssertion = async (options: PublicKeyCredentialRequestOptionsJSON) =>
  credentialToJSON(
    await ensureCredential(
      await navigator.credentials.get({publicKey: parseRequestOptions(options)})
    )
  );

const BACKUP_ELIGIBLE_FLAG = 0x08;

/**
 * Reads a new key's AAGUID and backup-eligible flag from its authenticator data: flags at byte 32,
 * the AAGUID at bytes 37–52. Returns `null` where the browser lacks `getAuthenticatorData`.
 */
export const readNewPasskeyInfo = (credential: PublicKeyCredential) => {
  const response = credential.response as AuthenticatorAttestationResponse;
  if (typeof response.getAuthenticatorData !== "function") return null;
  const data = new Uint8Array(response.getAuthenticatorData());
  if (data.length < 53) return null;
  const hex = Array.from(data.slice(37, 53), (byte) => byte.toString(16).padStart(2, "0")).join("");
  return {
    aaguid: hex.replace(/^(.{8})(.{4})(.{4})(.{4})/, "$1-$2-$3-$4-"),
    backupEligible: (data[32] & BACKUP_ELIGIBLE_FLAG) !== 0,
  };
};

/** Chrome ends its WebAuthn messages with a link to the spec. */
const withoutSpecLink = (message: string) => message.replace(/\s*See:\s*https?:\/\/\S+$/, "");

/** Turns a browser `DOMException` or a server error code from the security actions into a sentence. */
export const describeSecurityError = (error: unknown) => {
  if (error instanceof DOMException) {
    switch (error.name) {
      case "NotAllowedError": {
        // Covers a cancel and a timeout, but also an unfocused page or a site the browser refuses
        // WebAuthn on; only the browser's message tells them apart.
        const reason = withoutSpecLink(error.message);
        if (!reason) return "The request was cancelled or timed out.";
        return `The request did not complete. Your browser says: ${reason}`;
      }
      case "InvalidStateError":
        return "This security key is already registered.";
      case "SecurityError":
        return "Security keys cannot be used on this address.";
      default:
        return error.message;
    }
  }
  switch (refusalReason(error)) {
    case "invalid_challenge":
      return "That took too long. Please try again.";
    case "session_required":
      return "Please log out and back in before changing your security settings.";
    case "reverification_required":
      return "Please confirm it's you again.";
    case "no_second_factor":
      return "You have no security key registered.";
    case "no_session_awaiting_second_factor":
      return "Your login has timed out. Please go back and log in again.";
    case "rate_limit":
      return "Too many attempts. Please wait a minute and try again.";
    case "invalid_totp_code":
      return "This code is not valid. Wait for the next one.";
    case "totp_locked":
      return "Too many wrong codes. Use a recovery code, or try again later.";
    case "invalid_totp_setup":
      return "That took too long. Please start again.";
    case "no_totp":
      return "You have no authenticator app set up.";
    case "feature_disabled":
      return "Authenticator apps are not available for this organization yet.";
    case "invalid_credential":
    case "wrong_ceremony":
    case "challenge_mismatch":
    case "origin_not_allowed":
    case "rp_id_mismatch":
    case "user_not_present":
    case "unsupported_attestation":
    case "unsupported_public_key":
    case "unknown_credential":
    case "invalid_signature":
      return "The security key's response could not be verified.";
    default:
      return errorToString(error);
  }
};
